import React, { useState, useCallback, useContext, createContext, useMemo } from 'react';
import withSSR from '../../hoc/withSSR';
import fetchData from './fetch';
import styles from './index.less';

/**
 * CDN Host Context
 * 【注意】用于在所有子组件中共享 OSS 域名，避免逐层 props 传递。
 * - 值由 PayOdmPage 根组件从 fetchData 返回的 pageData.cdnHost 注入
 * - SSR 时从 Egg.js config.view.variable.CDN_HOST 获取
 * - CSR 时从 layout 注入的全局 const CDN_HOST 获取
 * - 子组件通过 useContext(CdnHostContext) 直接读取
 */
const CdnHostContext = createContext('');
/**
 * 【注意】多语言文案 Context
 * getConstants() 通过 window.i18next.t() 获取翻译词，英文作为 fallback。
 * 在 PayOdmPage 根组件中调用 getConstants() 一次，通过 Context 共享给所有子组件。
 * SSR 服务端渲染时返回英文默认值；客户端 hydrate 后返回对应语种翻译词。
 */
const ConstantsContext = createContext({});
import {
  getConstants,
  LINKS,
} from './constants';

/**
 * 判断是否为移动端（宽度 <= 700px）
 * @returns {boolean}
 */
function isMobileDevice() {
  if (typeof window === 'undefined') return false;
  return window.innerWidth <= 700;
}

/**
 * 【注意】通过读取 cjLoginToken cookie 判断用户是否已登录。
 * 项目中统一使用 cjLoginToken cookie 作为登录凭证：
 * - 服务端：ctx.cookies.get('cjLoginToken', { signed: false })（见 baseController.getLoginToken()）
 * - 客户端：读取 document.cookie 中的 cjLoginToken
 * 未登录时跳转 /login.html?target=<base64(目标URL)>，登录成功后自动回跳。
 * @param {string} name - Cookie 名称
 * @returns {string|null} Cookie 值
 */
function getCookie(name) {
  if (typeof document === 'undefined') return null;
  var match = document.cookie.match(new RegExp('(?:^|;\\s*)' + name + '=([^;]*)'));
  return match ? decodeURIComponent(match[1]) : null;
}

/**
 * CTA 按钮点击处理
 * 【注意】点击前先检查 cjLoginToken 登录态：
 * - 未登录 → 跳转 /login.html?target=<base64(目标URL)>，登录成功后自动回跳目标页
 * - 已登录 + PC/Pad → 直接跳转目标页
 * - 已登录 + Mobile → 弹出 Modal 提示（移动端暂不支持该功能）
 * @param {string} url - 跳转地址
 * @param {Function} setShowModal - 设置 Modal 显示状态
 */
function handleCtaClick(url, setShowModal) {
  if (typeof window === 'undefined') return;
  var token = getCookie('cjLoginToken');
  if (!token) {
    var targetUrl = window.location.origin + url;
    window.location.href = '/login.html?target=' + btoa(targetUrl);
    return;
  }
  if (isMobileDevice()) {
    setShowModal(true);
  } else {
    window.location.href = url;
  }
}

/* ========== HeroBanner ========== */
/**
 * 【注意】背景图通过 CSS 自定义属性（--hero-bg-*）传递 cdnHost + OSS 路径给 Less，
 * 因为 CSS url() 无法直接拼接 JS 变量。三端背景分别为：
 * - --hero-bg-pc：PC端背景图
 * - --hero-bg-tablet：Pad端背景图
 * - --hero-bg-mobile：Mobile端背景图
 * Less 中通过 var(--hero-bg-*) 在对应媒体查询中引用。
 */
function HeroBanner({ onCtaClick }) {
  const cdnHost = useContext(CdnHostContext);
  const { HERO } = useContext(ConstantsContext);
  const heroBgVars = {
    '--hero-bg-pc': `url(${cdnHost}/resources/egg/odm/hero_banner_bg_pc.jpg)`,
    '--hero-bg-tablet': `url(${cdnHost}/resources/egg/odm/hero_banner_bg_tablet.jpg)`,
    '--hero-bg-mobile': `url(${cdnHost}/resources/egg/odm/hero_banner_bg_mobile.jpg)`,
  };
  return (
    <section className={styles['hero-banner']} style={heroBgVars}>
      {/* <div className={styles['hero-bg-circle-1']} /> */}
      <div className={styles['hero-container']}>
        <div className={styles['hero-content']}>
          <div className={styles['hero-badges']}>
            {HERO.trustBadges.map(function (badge, i) {
              return (
                <React.Fragment key={i}>
                  {<span className={styles['badge-dot']}>·</span>}
                  <span className={styles['badge-text']}>{badge}</span>
                </React.Fragment>
              );
            })}
          </div>
          <h1 className={styles['hero-title']}>
            {HERO.title}
            <br />
            <span className={styles['hero-title-highlight']}>
              {HERO.titleHighlight}
            </span>
          </h1>
          <p className={styles['hero-subtitle']}>{HERO.subtitle}</p>
          <div className={styles['hero-cta-group']}>
            <button
              className={styles['btn-paid']}
              onClick={function () {
                onCtaClick(LINKS.paidOdm);
              }}
            >
              {HERO.ctaPaid}
            </button>
            <button
              className={styles['btn-free']}
              onClick={function () {
                onCtaClick(LINKS.freeOdm);
              }}
            >
              {HERO.ctaFree}
            </button>
          </div>
        </div>
        <div className={styles['hero-image']}>
          <img
            src={`${cdnHost}/resources/egg/odm/hero_banner_person.png`}
            alt="Premium Paid ODM"
            loading="lazy"
          />
        </div>
      </div>
    </section>
  );
}

/* ========== ComparisonTable ========== */
function ComparisonTable({ onCtaClick }) {
  const { COMPARISON } = useContext(ConstantsContext);
  const [activeTab, setActiveTab] = useState('paid');

  return (
    <section className={styles['comparison-section']}>
      <div className={styles['comparison-container']}>
        <div className={styles['section-header']}>
          <h2>{COMPARISON.title}</h2>
          <p>{COMPARISON.subtitle}</p>
        </div>

        {/* PC/Pad 表格 */}
        <div className={styles['comparison-table-wrapper']}>
          <table className={styles['comparison-table']}>
            <thead>
              <tr>
                <th>Service</th>
                <th>{COMPARISON.freeOdm.header}</th>
                <th className={styles['paid-header']}>
                  <i className={'iconfont iconhuangguan'} style={{ fontSize: '24px', marginRight: '8px' }} />
                  {COMPARISON.paidOdm.header}
                </th>
              </tr>
            </thead>
            <tbody>
              {COMPARISON.serviceLabels.map(function (label, i) {
                const paidItem = COMPARISON.paidOdm.items[i];
                return (
                  <tr key={i} className={i % 2 === 0 ? styles['row-even'] : ''}>
                    <td className={styles['service-label']}>{label}</td>
                    <td>
                      <div className={styles['cell-content']}>
                        <span className={styles['check-icon'] + ' ' + styles['check-gray']}>✓</span>
                        <span>{COMPARISON.freeOdm.items[i]}</span>
                      </div>
                    </td>
                    <td className={styles['paid-cell']}>
                      <div className={styles['cell-content']}>
                        <span className={styles['check-icon'] + ' ' + styles['check-orange']}>✓</span>
                        {paidItem.highlightAtEnd ? (
                          <span>
                            <span>{paidItem.rest}</span>
                            <span className={styles['highlight-text']}>{paidItem.highlight}</span>
                          </span>
                        ) : (
                          <span>
                            <span className={styles['highlight-text']}>{paidItem.highlight}</span>
                            <span>{paidItem.rest}</span>
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Mobile Tab 切换 */}
        <div className={styles['comparison-mobile']}>
          <div className={styles['tab-header']}>
            <button
              className={
                styles['tab-btn'] +
                (activeTab === 'paid' ? ' ' + styles['tab-active'] : '')
              }
              onClick={function () {
                setActiveTab('paid');
              }}
            >
              <i className={'iconfont iconhuangguan'} />
              <span> Paid </span>
            </button>
            <button
              className={
                styles['tab-btn'] +
                (activeTab === 'free' ? ' ' + styles['tab-active'] : '')
              }
              onClick={function () {
                setActiveTab('free');
              }}
            >
              Free
            </button>
          </div>
          <div className={styles['tab-content'] + (activeTab === 'free' ? ' ' + styles['tab-content-free'] : '')}>
            <h3>
              {activeTab === 'paid'
                ? COMPARISON.paidOdm.header
                : COMPARISON.freeOdm.header}
            </h3>
            {COMPARISON.serviceLabels.map(function (label, i) {
              const isPaid = activeTab === 'paid';
              const item = isPaid
                ? COMPARISON.paidOdm.items[i]
                : COMPARISON.freeOdm.items[i];
              return (
                <div key={i} className={styles['tab-item']}>
                  <div className={styles['tab-item-label']}>{label}</div>
                  <div className={styles['tab-item-value']}>
                    {isPaid ? (
                      item.highlightAtEnd ? (
                        <span>
                          <span>{item.rest}</span>
                          <span className={styles['highlight-text']}>{item.highlight}</span>
                        </span>
                      ) : (
                        <span>
                          <span className={styles['highlight-text']}>{item.highlight}</span>
                          <span>{item.rest}</span>
                        </span>
                      )
                    ) : (
                      item
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <p className={styles['comparison-footer']}>{COMPARISON.footerNote}</p>
        {/* <div className={styles['comparison-cta']}>
          <button
            className={styles['btn-paid']}
            onClick={function () {
              onCtaClick(LINKS.paidOdm);
            }}
          >
            {COMPARISON.ctaPaid}
          </button>
          <button
            className={styles['btn-free']}
            onClick={function () {
              onCtaClick(LINKS.freeOdm);
            }}
          >
            {COMPARISON.ctaFree}
          </button>
        </div> */}
      </div>
    </section>
  );
}

/* ========== WhoShouldChoose ========== */
function WhoShouldChoose({ onCtaClick }) {
  const cdnHost = useContext(CdnHostContext);
  const { WHO_SHOULD_CHOOSE } = useContext(ConstantsContext);
  return (
    <section className={styles['who-section']}>
      <div className={styles['who-container']}>
        <h2>{WHO_SHOULD_CHOOSE.title}</h2>
        <div className={styles['who-grid']}>
          <div className={styles['who-content']}>
            <ul className={styles['who-list']}>
              {WHO_SHOULD_CHOOSE.items.map(function (item, i) {
                return (
                  <li key={i}>
                    <span className={styles['who-icon']}>
                      <img
                        src={`${cdnHost}/resources/egg/odm/who_icon${i + 1}.png`}
                        alt="Who Should Choose"
                        loading="lazy"
                      />
                    </span>
                    <span>{item}</span>
                  </li>
                );
              })}
            </ul>
            {/* <button
              className={styles['btn-paid']}
              onClick={function () {
                onCtaClick(LINKS.paidOdm);
              }}
            >
              {WHO_SHOULD_CHOOSE.ctaPaid}
            </button> */}
          </div>
          <div className={styles['who-image']}>
            <img
              src={`${cdnHost}/resources/egg/odm/person1.png`}
              alt="Who Should Choose"
              loading="lazy"
            />
          </div>
        </div>
      </div>
    </section>
  );
}

/* ========== MainAdvantages ========== */
function MainAdvantages() {
  const cdnHost = useContext(CdnHostContext);
  const { ADVANTAGES } = useContext(ConstantsContext);
  return (
    <section className={styles['advantages-section']}>
      <div className={styles['advantages-container']}>
        <div className={styles['section-header']}>
          <h2>{ADVANTAGES.title}</h2>
          <p>{ADVANTAGES.subtitle}</p>
        </div>
        {ADVANTAGES.sections.map(function (section, sIdx) {
          const isReversed = sIdx % 2 === 1;
          return (
            <div
              key={sIdx}
              className={
                styles['advantage-block'] +
                (isReversed ? ' ' + styles['advantage-reversed'] : '')
              }
            >
              <div className={styles['advantage-content']}>
                <h3>{section.title}</h3>
                <ul>
                  {section.items.map(function (item, iIdx) {
                    return (
                      <li key={iIdx}>
                        <i className={'iconfont icongouxuan ' + styles['advantage-check']} />
                        <div className={styles['advantage-text']}>
                          <strong>{item.label}</strong>
                          <span>{item.desc}</span>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
              <div className={styles['advantage-image']}>
                <img
                  src={`${cdnHost}/resources/egg/odm/person${sIdx + 2}.png`}
                  alt={section.title}
                  loading="lazy"
                />
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

/* ========== PaidOdmSteps ========== */
function PaidOdmSteps() {
  const cdnHost = useContext(CdnHostContext);
  const { STEPS } = useContext(ConstantsContext);
  return (
    <section className={styles['steps-section']}>
      <div className={styles['steps-container']}>
        <div className={styles['section-header']}>
          <h2>{STEPS.title}</h2>
          <p>{STEPS.subtitle}</p>
        </div>
        <div className={styles['steps-grid']}>
          {STEPS.items.map(function (item, i) {
            return (
              <div key={i} className={styles['step-item']}>
                <div className={styles['step-icon']}>
                  <img
                    src={`${cdnHost}/resources/egg/odm/step${i + 1}.png`}
                    alt={item.step}
                    loading="lazy"
                  />
                </div>
                <div className={styles['step-label']}>{item.step}</div>
                <div className={styles['step-text']}>{item.text}</div>
              </div>
            );
          })}
        </div>
        <p className={styles['steps-slogan']}>
          <span>{STEPS.slogan.text}</span>
          <span className={styles['slogan-highlight']}>{STEPS.slogan.highlight}</span>
        </p>
      </div>
    </section>
  );
}

/* ========== CtaBanner ========== */
function CtaBanner({ onCtaClick }) {
  const cdnHost = useContext(CdnHostContext);
  const { CTA_BANNER } = useContext(ConstantsContext);
  const ctaBannerBgVars = {
    '--cta-banner-bg-pc-tablet': `url(${cdnHost}/resources/egg/odm/cta_banner_bg_pc_tablet.jpg)`,
    '--cta-banner-bg-mobile': `url(${cdnHost}/resources/egg/odm/cta_banner_bg_mobile.jpg)`,
  };
  return (
    <section className={styles['cta-section']}>
      <div 
        className={styles['cta-banner']}
      >
        <div className={styles['cta-banner-bg']} style={ctaBannerBgVars} />
        <div className={styles['cta-banner-overlay']} />
        <div className={styles['cta-banner-content']}>
          <h2>{CTA_BANNER.title}</h2>
          <p>{CTA_BANNER.subtitle}</p>
          <button
            className={styles['btn-paid']}
            onClick={function () {
              onCtaClick(LINKS.paidOdm);
            }}
          >
            {CTA_BANNER.cta}
          </button>
        </div>
      </div>
    </section>
  );
}

/* ========== FaqSection ========== */
function FaqSection({ onCtaClick }) {
  const { FAQ, CTA_BANNER } = useContext(ConstantsContext);
  const [openItems, setOpenItems] = useState([]);

  const toggleItem = useCallback(
    function (index) {
      setOpenItems(function (prev) {
        if (prev.indexOf(index) > -1) {
          return prev.filter(function (i) {
            return i !== index;
          });
        }
        return prev.concat([index]);
      });
    },
    []
  );

  return (
    <section className={styles['faq-section']}>
      <div className={styles['faq-container']}>
        <div className={styles['section-header']}>
          <h2>{FAQ.title}</h2>
          <p>{FAQ.subtitle}</p>
        </div>
        <div className={styles['faq-list']}>
          {FAQ.items.map(function (item, i) {
            const isOpen = openItems.indexOf(i) > -1;
            return (
              <div
                key={i}
                className={
                  styles['faq-item'] +
                  (isOpen ? ' ' + styles['faq-item-open'] : '')
                }
              >
                <button
                  className={styles['faq-question']}
                  onClick={function () {
                    toggleItem(i);
                  }}
                >
                  <span>{item.question}</span>
                  <i className={'iconfont ' + (isOpen ? 'iconshouqi2' : 'iconzhankai2') + ' ' + styles['faq-arrow']} />
                </button>
                {isOpen && (
                  <div className={styles['faq-answer']}>{item.answer}</div>
                )}
              </div>
            );
          })}
        </div>
        {/* <button
          className={styles['btn-paid'] + ' ' + styles['faq-cta']}
          onClick={function () {
            onCtaClick(LINKS.paidOdm);
          }}
        >
          {CTA_BANNER.spo}
        </button> */}
      </div>
    </section>
  );
}

/* ========== Modal 组件 (Mobile端提示) ========== */
function MobileModal({ visible, onClose }) {
  const { MOBILE_PROMPT } = useContext(ConstantsContext);
  if (!visible) return null;
  return (
    <div className={styles['modal-overlay']} onClick={onClose}>
      <div
        className={styles['modal-content']}
        onClick={function (e) {
          e.stopPropagation();
        }}
      >
        <h3 className={styles['modal-title']}>{MOBILE_PROMPT.title}</h3>
        <p className={styles['modal-message']}>{MOBILE_PROMPT.message}</p>
        <button className={styles['modal-btn']} onClick={onClose}>
          {MOBILE_PROMPT.buttonText}
        </button>
      </div>
    </div>
  );
}

/* ========== 页面入口组件 ========== */
function PayOdmPage(props) {
  const pageData = props.pageData || {};
  const cdnHost = pageData.cdnHost || (typeof CDN_HOST !== 'undefined' ? CDN_HOST : '');

  /**
   * 【注意】useMemo 确保 getConstants() 只在组件渲染时调用一次，
   * 此时 window.i18next 已初始化（客户端），或返回英文默认值（SSR 服务端）。
   */
  const constants = useMemo(function () {
    return getConstants();
  }, []);

  const [showModal, setShowModal] = useState(false);

  const onCtaClick = useCallback(
    function (url) {
      handleCtaClick(url, setShowModal);
    },
    [setShowModal]
  );

  const closeModal = useCallback(function () {
    setShowModal(false);
  }, []);

  return (
    <CdnHostContext.Provider value={cdnHost}>
      <ConstantsContext.Provider value={constants}>
        <div className={styles['odm-page']}>
          <HeroBanner onCtaClick={onCtaClick} />
          <ComparisonTable onCtaClick={onCtaClick} />
          <WhoShouldChoose onCtaClick={onCtaClick} />
          <MainAdvantages />
          <PaidOdmSteps />
          <CtaBanner onCtaClick={onCtaClick} />
          <FaqSection onCtaClick={onCtaClick} />
          <MobileModal visible={showModal} onClose={closeModal} />
        </div>
      </ConstantsContext.Provider>
    </CdnHostContext.Provider>
  );
}

PayOdmPage.fetch = fetchData;

export default withSSR(PayOdmPage);
